import {
	Breakpoints,
	BreakpointWrapper,
	Container,
	ContainerGutter,
	ContainerPadding,
	CTA,
	Image,
	Text,
	TextAlignment,
	TokenTextAppearance,
} from '@volkswagen-onehub/components-core';
import { LayerHandleV2 } from '@volkswagen-onehub/layer-manager';
import React from 'react';
import styled from 'styled-components';
import { DataVehicles, ErrorType, Vehicle, VehicleStatus } from '@esportal/portal-service';
import { CreateVehicle } from './create-vehicle';
import { DeleteVehicle } from './deleteVehicle';
import { PersonVehiclesCTAs } from './person-vehicles-CTAs';
import { CarPlusIcon } from '../utils/car-plus.icon';
import { useTrackingManager } from '../../../tracking/useTrackingManager';
import { formatOwnership } from '../../../tracking/trackingHelper';
import { PortalContext } from '../../../context/PortalContext';

const CarContainer = styled.div<{ vehicleStatus: VehicleStatus }>`
	border: ${(props) => props.vehicleStatus === VehicleStatus.TO_BE_DELETED && '2px dashed #E4002C'};
	background-color: ${(props) => props.vehicleStatus === VehicleStatus.TO_BE_DELETED && '#FFF3F5'};

	border: ${(props) => props.vehicleStatus === VehicleStatus.TO_BE_CREATED && '2px dashed #6A767D'};
	background-color: ${(props) => props.vehicleStatus === VehicleStatus.TO_BE_CREATED && '#F3FFF4'};

	border: ${(props) => props.vehicleStatus === VehicleStatus.ORDER && '2px dashed #00B0F0'};
	background-color: ${(props) => props.vehicleStatus === VehicleStatus.ORDER && '#F3F9FF'};

	margin-bottom: ${(props) =>
		props.vehicleStatus !== VehicleStatus.READY &&
		props.vehicleStatus !== VehicleStatus.DELETED &&
		'var(--size-dynamic0200)'};

	padding-top: 10px;
	border-radius: 16px;
`;

const FlexContainer = styled.div`
	& div > div > div:first-child {
		flex: 1;
	}

	& div > div > div:last-child {
		flex: 1;
	}
`;

const CTAStyledAdd = styled(CTA)`
	width: 64px;
	height: 64px;

	& svg {
		width: 32px;
		height: 32px;
	}
`;

const CarImage = styled(Image)<{ vehicleStatus: VehicleStatus; mediaAsset: boolean }>`
	opacity: ${(props) =>
		props.vehicleStatus === VehicleStatus.TO_BE_DELETED || props.vehicleStatus === VehicleStatus.TO_BE_CREATED
			? '0.5'
			: '1'};
	position: relative;

	-ms-transform: ${(props) =>
		props.mediaAsset ? 'scale(1.4)' : props.vehicleStatus !== VehicleStatus.ORDER && 'scale(1.4)'}; /* IE 9 */
	-webkit-transform: ${(props) =>
		props.mediaAsset ? 'scale(1.4)' : props.vehicleStatus !== VehicleStatus.ORDER && 'scale(1.4)'}; /* Safari 3-8 */
	transform: ${(props) =>
		props.mediaAsset ? 'scale(1.4)' : props.vehicleStatus !== VehicleStatus.ORDER && 'scale(1.4)'};

	@media screen and (min-width: 560px) {
		-ms-transform: ${(props) =>
			props.mediaAsset ? 'scale(1.6)' : props.vehicleStatus !== VehicleStatus.ORDER && 'scale(1.6)'}; /* IE 9 */
		-webkit-transform: ${(props) =>
			props.mediaAsset ? 'scale(1.6)' : props.vehicleStatus !== VehicleStatus.ORDER && 'scale(1.6)'}; /* Safari 3-8 */
		transform: ${(props) =>
			props.mediaAsset ? 'scale(1.6)' : props.vehicleStatus !== VehicleStatus.ORDER && 'scale(1.6)'};
	}
`;

const FlexPersonVehicleContainer = styled.div`
	display: flex;
	flex-wrap: wrap;
	justify-content: center;
	align-items: flex-start;

	@media screen and (min-width: 1280px) {
		overflow: hidden;

		flex-direction: row;
		flex-wrap: wrap;

		& > div {
			flex: 1 1 calc(10%);
			margin: var(--size-grid001);
			margin-top: 0;

			@media screen and (min-width: 1280px) and (max-width: 1919px) {
				flex: 1 1 calc(30%);
				margin: 46px;
				margin-top: 0;
			}

			@media screen and (min-width: 1920px) {
				flex: 1 1 calc(25%);
				margin: 56px;
				margin-top: 0;
			}
		}
	}
`;

const EmptyContainer = styled.div`
	height: 30px;
`;

const ClickableContainer = styled.div`
	cursor: pointer;
`;

const FlexPersonVehicleWrapper = ({ children }: { children: React.ReactElement | undefined }): React.ReactElement => {
	return (
		<>
			<BreakpointWrapper min={Breakpoints.default} max={Breakpoints.b1280}>
				<Container
					data-testid="flex-person-vehicle"
					padding={{
						left: {
							[Breakpoints.default]: ContainerPadding.grid001,
							[Breakpoints.b560]: ContainerPadding.grid002,
							[Breakpoints.b960]: ContainerPadding.grid003,
						},
						right: {
							[Breakpoints.default]: ContainerPadding.grid001,
							[Breakpoints.b560]: ContainerPadding.grid002,
							[Breakpoints.b960]: ContainerPadding.grid003,
						},
					}}
					wrap={'always'}
					horizontalAlign={'center'}
					stretchContent
				>
					{children}
				</Container>
			</BreakpointWrapper>
			<BreakpointWrapper min={Breakpoints.b1280}>
				<FlexPersonVehicleContainer className="flex-person-vehicle-container">{children}</FlexPersonVehicleContainer>
			</BreakpointWrapper>
		</>
	);
};

export const PersonVehicles: React.FC<{
	vehicles: DataVehicles | null;
	setIsJourneyLayerVisible: React.Dispatch<React.SetStateAction<boolean>>;
	setIsAlertLayerVisible: React.Dispatch<React.SetStateAction<boolean>>;
	layerRef: React.RefObject<LayerHandleV2<any>>;
	setErrorType: React.Dispatch<React.SetStateAction<ErrorType>>;
}> = ({ vehicles, setIsJourneyLayerVisible, setIsAlertLayerVisible, layerRef, setErrorType }) => {
	const { portalService } = React.useContext(PortalContext);
	const [vehicleToDelete, setVehicleToDelete] = React.useState<Vehicle | null>(null);
	const [isConfirming, setIsConfirming] = React.useState<string | null>(null);
	const [deleteProcess, setDeleteProcess] = React.useState<boolean>(false);
	const [createVehicleProcess, setCreateVehicleProcess] = React.useState<boolean>(false);

	const trackingManager = useTrackingManager();

	React.useEffect(() => {
		if (!deleteProcess && !createVehicleProcess)
			trackingManager.trackClubVWLayerLoad(
				{ contentId: 'Person Vehicles' },
				{ vehicle: formatOwnership(vehicles?.vehicles) },
			);
	}, [vehicles]);

	const handleAddVehicleOnClick = () => {
		setCreateVehicleProcess(true);
		trackingManager.trackClubVWButtonClick(
			'Añadir coche',
			{ contentId: 'Add vehicle' },
			{ vehicle: formatOwnership(vehicles?.vehicles) },
		);
	};

	const checkConfirmedVehicles = () => {
		if (vehicles) {
			return vehicles?.vehicles.some((vehicle) => vehicle.status === VehicleStatus.TO_BE_CONFIRMED);
		}

		return false;
	};

	const handleConfirm = (vehicle: Vehicle) => {
		setIsConfirming(vehicle.frame);

		portalService.confirmPersonVehicle(vehicle.frame).subscribe({
			next: (updatedVehicles: DataVehicles | null) => {
				if (updatedVehicles) {
					vehicle.status = VehicleStatus.READY;
				}

				const vehiclesLeftToBeConfirmed = checkConfirmedVehicles();

				if (!vehiclesLeftToBeConfirmed) {
					setIsJourneyLayerVisible(false);

					if (layerRef?.current) {
						layerRef?.current.close();
						(layerRef.current as any) = null;
					}
				}

				setIsConfirming(null);
			},
			error: () => {
				setIsJourneyLayerVisible(false);
				setIsAlertLayerVisible(true);
				setErrorType(ErrorType.ConfirmVehicle);

				if (layerRef?.current) {
					layerRef?.current.close();
					(layerRef.current as any) = null;
				}

				setIsConfirming(null);
			},
		});
	};

	const openDelete = (vehicle: Vehicle) => {
		setDeleteProcess(true);
		setVehicleToDelete(vehicle);
	};

	const handleCloseOnClick = () => {
		setIsJourneyLayerVisible(false);
		trackingManager.trackClubVWLayerCloseClick('Cerrar', {
			contentId: 'Person Vehicles',
		});

		if (layerRef?.current) {
			layerRef?.current.close();
			(layerRef.current as any) = null;
		}
	};

	return (
		<>
			{deleteProcess ? (
				<DeleteVehicle
					vehicleToDelete={vehicleToDelete}
					vehicles={vehicles?.vehicles ?? null}
					setDeleteProcess={setDeleteProcess}
					setIsJourneyLayerVisible={setIsJourneyLayerVisible}
					setIsAlertLayerVisible={setIsAlertLayerVisible}
					layerRef={layerRef}
					setErrorType={setErrorType}
				/>
			) : createVehicleProcess ? (
				<CreateVehicle
					setIsJourneyLayerVisible={setIsJourneyLayerVisible}
					setIsAlertLayerVisible={setIsAlertLayerVisible}
					layerRef={layerRef}
					setErrorType={setErrorType}
				/>
			) : (
				<>
					<Container
						horizontalAlign={'center'}
						padding={{
							right: {
								[Breakpoints.default]: ContainerPadding.grid001,
								[Breakpoints.b560]: ContainerPadding.grid002,
								[Breakpoints.b960]: ContainerPadding.grid001,
							},
							bottom: ContainerPadding.dynamic0200,
							left: {
								[Breakpoints.default]: ContainerPadding.grid001,
								[Breakpoints.b560]: ContainerPadding.grid002,
								[Breakpoints.b960]: ContainerPadding.grid001,
							},
						}}
					>
						<Text appearance={TokenTextAppearance.headline300} textAlign={TextAlignment.center} bold>
							¿Estos son los Volkswagen que conduces?
						</Text>
						<Container
							padding={{
								top: ContainerPadding.dynamic0050,
							}}
						>
							<Text appearance={TokenTextAppearance.copy200} textAlign={TextAlignment.center}>
								Confirma tus coches para desbloquear nuevas funcionalidades digitales.
							</Text>
						</Container>
					</Container>
					<FlexPersonVehicleWrapper>
						<>
							{vehicles?.vehicles.map((vehicle, index) => {
								return (
									<CarContainer className="car-container" key={index} vehicleStatus={vehicle.status}>
										<FlexContainer className="flex-container">
											<Container
												padding={
													vehicle.status === VehicleStatus.TO_BE_DELETED ||
													vehicle.status === VehicleStatus.TO_BE_CREATED ||
													vehicle.status === VehicleStatus.ORDER
														? {
																top: ContainerPadding.static150,
																right: ContainerPadding.static150,
																bottom: {
																	[Breakpoints.default]: ContainerPadding.static200,
																},
																left: ContainerPadding.static150,
															}
														: ContainerPadding.static0
												}
												wrap={{ [Breakpoints.default]: 'never', [Breakpoints.b1280]: 'always' }}
												verticalAlign={'center'}
												horizontalAlign={'center'}
												shrinkContent
											>
												<CarImage
													src={
														vehicle.status !== VehicleStatus.ORDER
															? vehicle.mediaAssets?.Front_Left ||
																'https://vgedonehubappsstaweupro.blob.core.windows.net/storage/images-portal/car_silhouette.png'
															: vehicle.mediaAssets?.Front_Left ||
																'https://vgedonehubappsstaweupro.blob.core.windows.net/storage/images-portal/order_car.gif'
													}
													height="80"
													alt="VWCar"
													objectFit="contain"
													vehicleStatus={vehicle.status}
													mediaAsset={!!vehicle.mediaAssets?.Front_Left}
												/>
												<Container
													wrap={'always'}
													stretchContent
													padding={{
														top: ContainerPadding.static150,
													}}
												>
													{vehicle.status !== VehicleStatus.ORDER ? (
														<>
															{vehicle.comercialDenomination ? (
																<Text appearance={TokenTextAppearance.bigcopy100} textAlign={TextAlignment.center} bold>
																	{vehicle.comercialDenomination}
																</Text>
															) : (
																<EmptyContainer />
															)}
															<Text appearance={TokenTextAppearance.copy200} textAlign={TextAlignment.center}>
																{vehicle.license}
															</Text>
														</>
													) : (
														<Container
															padding={{
																bottom: { [Breakpoints.b1280]: ContainerPadding.static150 },
															}}
															wrap={'never'}
														>
															<Text appearance={TokenTextAppearance.bigcopy100} textAlign={TextAlignment.center}>
																Tu nuevo{' '}
																<span style={{ display: 'inline-block' }}>
																	<Text
																		appearance={TokenTextAppearance.bigcopy100}
																		textAlign={TextAlignment.center}
																		bold
																	>
																		{vehicle.comercialDenomination ? vehicle.comercialDenomination : 'Volkswagen'}
																	</Text>
																</span>
															</Text>
														</Container>
													)}
												</Container>
											</Container>
										</FlexContainer>

										{vehicle.status === VehicleStatus.TO_BE_DELETED ||
										vehicle.status === VehicleStatus.TO_BE_CREATED ||
										vehicle.status === VehicleStatus.ORDER ? (
											<Container
												wrap={'always'}
												horizontalAlign={'center'}
												padding={{ bottom: ContainerPadding.static300 }}
											>
												<Text appearance={TokenTextAppearance.copy100} textAlign={TextAlignment.center} bold>
													{vehicle.status === VehicleStatus.TO_BE_DELETED && 'Eliminando coche'}
													{vehicle.status === VehicleStatus.TO_BE_CREATED && 'Añadiendo coche'}
													{vehicle.status === VehicleStatus.ORDER && 'Pedido en curso'}
												</Text>
												<Text appearance={TokenTextAppearance.copy100} textAlign={TextAlignment.center}>
													{(vehicle.status === VehicleStatus.TO_BE_DELETED ||
														vehicle.status === VehicleStatus.TO_BE_CREATED) &&
														'Puede tardar unos días en actualizarse.'}
												</Text>
											</Container>
										) : (
											<PersonVehiclesCTAs
												vehicle={vehicle}
												isConfirming={isConfirming}
												handleConfirm={handleConfirm}
												openDelete={openDelete}
											/>
										)}
									</CarContainer>
								);
							})}
						</>
					</FlexPersonVehicleWrapper>
					<Container
						padding={{
							bottom: ContainerPadding.dynamic0200,
						}}
						gutter={ContainerGutter.dynamic0100}
						horizontalAlign={'center'}
						verticalAlign={'center'}
					>
						<CTAStyledAdd
							tag="button"
							emphasis="primary"
							onClick={handleAddVehicleOnClick}
							aria-label="Añadir coche"
							icon={<CarPlusIcon />}
						/>
						<ClickableContainer onClick={handleAddVehicleOnClick}>
							<Text appearance={TokenTextAppearance.bigcopy100}>Añadir coche</Text>
						</ClickableContainer>
					</Container>
					<Container
						horizontalAlign={'center'}
						padding={{
							bottom: ContainerPadding.dynamic0200,
						}}
					>
						<CTA tag="button" emphasis="secondary" size="large" stretchContent onClick={handleCloseOnClick}>
							<Container padding={{ right: ContainerPadding.static350, left: ContainerPadding.static350 }}>
								<Text>Cerrar</Text>
							</Container>
						</CTA>
					</Container>
				</>
			)}
		</>
	);
};
